import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Button from './ui/Button';
import Input from './ui/Input';
import Alert from './ui/Alert';
import Modal from './ui/Modal';

const ChangePasswordModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (formData.newPassword === formData.currentPassword) {
      setError(t('modals.changePassword.mustMatch'));
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('modals.changePassword.mustMatch'));
      return;
    }

    if (formData.newPassword.length < 8) {
      setError(t('modals.changePassword.tooShort'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/change-password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      // Schimbarea parolei incrementează tokenVersion pe server (revocă toate sesiunile vechi).
      // Salvăm token-ul nou emis pentru sesiunea curentă, altfel reload-ul de mai jos ar folosi
      // token-ul vechi → 401 TOKEN_REVOKED → delogare imediat după setarea parolei.
      if (response.data?.token) {
        localStorage.setItem('authToken', response.data.token);
      }

      const user = JSON.parse(localStorage.getItem('user'));
      user.firstLogin = false;
      user.passwordChanged = true;
      localStorage.setItem('user', JSON.stringify(user));

      setSuccess(true);

      setTimeout(() => {
        if (onSuccess) onSuccess();
        window.location.reload();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || t('toasts.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      closable={!loading && !success}
      title={t('modals.changePassword.title')}
      size="sm"
      footer={
        success ? null : (
          <div className="flex gap-3 justify-end w-full">
            {onClose && (
              <Button variant="secondary" onClick={onClose} disabled={loading}>
                {t('common.cancel')}
              </Button>
            )}
            <Button variant="primary" onClick={handleSubmit} loading={loading} disabled={loading}>
              {t('modals.changePassword.saveBtn')}
            </Button>
          </div>
        )
      }
    >
      {success ? (
        <div className="text-center py-4">
          <div className="w-14 h-14 bg-accent-100 dark:bg-accent-950/50 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg
              className="w-7 h-7 text-accent-600 dark:text-accent-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {t('modals.changePassword.success')}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('modals.changePassword.subtitle')}
          </p>
          <div className="flex items-start gap-2 rounded-lg bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 px-3 py-2">
            <svg
              className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-xs text-blue-700 dark:text-blue-300 leading-snug">
              {t('modals.changePassword.recommendation')}
            </p>
          </div>
          <Input
            label={t('modals.changePassword.currentPwd') + ' *'}
            type="password"
            required
            value={formData.currentPassword}
            onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
          />
          <Input
            label={t('modals.changePassword.newPwd') + ' *'}
            type="password"
            required
            value={formData.newPassword}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
            helper={t('modals.changePassword.tooShort')}
          />
          <Input
            label={t('modals.changePassword.confirmPwd') + ' *'}
            type="password"
            required
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          />
          {error && <Alert type="error">{error}</Alert>}
          <button type="submit" className="hidden" />
        </form>
      )}
    </Modal>
  );
};

export default ChangePasswordModal;
